/* Auction Clearance v1.1 — progressive navigation and decorative motion only.
   Native details, normal links and all core commerce still work without this file. */
(() => {
  'use strict';
  const desktop = window.matchMedia('(min-width: 990px)');
  const pointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mode = () => document.body.dataset.acMotion || 'enhanced';
  const depthEnabled = () => mode() === 'enhanced' && pointer.matches && !reduce.matches;
  const hoverTimers = new WeakMap();
  const frames = new WeakMap();
  const initialized = new WeakSet();
  const revealed = new WeakSet();
  let observer;
  const clearTimers = (el) => {
    const timers = hoverTimers.get(el);
    if (timers) { clearTimeout(timers.enter); clearTimeout(timers.leave); }
  };
  const closeDesktopMenus = () => document.querySelectorAll('.ac-header .ac-mega[open]').forEach(el => { clearTimers(el); el.open = false; });
  const measureHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const bottom = Math.max(0, Math.ceil(header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--ac-menu-bottom', `${bottom}px`);
  };
  const bindMenu = (menu) => {
    if (initialized.has(menu)) return;
    initialized.add(menu);
    const summary = menu.querySelector(':scope > summary');
    if (!summary) return;
    menu.addEventListener('pointerenter', () => {
      if (!desktop.matches || !pointer.matches || !menu.closest('.ac-header')) return;
      clearTimers(menu);
      const enter = setTimeout(() => {
        if (!menu.isConnected || !menu.matches(':hover')) return;
        measureHeader();
        document.querySelectorAll('.ac-header .ac-mega[open]').forEach(other => { if (other !== menu) { clearTimers(other); other.open = false; } });
        menu.open = true;
      }, 190);
      hoverTimers.set(menu, { enter });
    });
    menu.addEventListener('pointerleave', () => {
      clearTimers(menu);
      if (!desktop.matches || !pointer.matches || !menu.closest('.ac-header')) return;
      const leave = setTimeout(() => {
        if (!menu.contains(document.activeElement)) menu.open = false;
      }, 260);
      hoverTimers.set(menu, { leave });
    });
    summary.addEventListener('click', () => { clearTimers(menu); measureHeader(); });
    menu.addEventListener('focusout', () => {
      setTimeout(() => {
        if (desktop.matches && menu.closest('.ac-header') && !menu.contains(document.activeElement) && !menu.matches(':hover')) menu.open = false;
      }, 0);
    });
    menu.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.open) {
        event.preventDefault(); event.stopPropagation();
        clearTimers(menu); menu.open = false; summary.focus();
      }
    });
    menu.addEventListener('toggle', () => {
      if (menu.open) {
        measureHeader();
        const mobile = menu.closest('.ac-mobile-menu');
        if (mobile) mobile.querySelectorAll('.ac-mega[open]').forEach(other => { if (other !== menu) other.open = false; });
      }
    });
  };
  const resetDepth = (el) => {
    const id = frames.get(el); if (id) cancelAnimationFrame(id);
    ['--ac-depth-x', '--ac-depth-y', '--ac-depth-r', '--ac-card-x', '--ac-card-y'].forEach(key => el.style.removeProperty(key));
  };
  const bindDepth = (el) => {
    if (initialized.has(el)) return;
    initialized.add(el);
    el.addEventListener('pointermove', (event) => {
      if (!depthEnabled() || event.pointerType === 'touch') return;
      const old = frames.get(el); if (old) cancelAnimationFrame(old);
      frames.set(el, requestAnimationFrame(() => {
        if (!el.isConnected) return;
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - .5) * 2));
        const y = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height - .5) * 2));
        if (el.hasAttribute('data-depth-stage')) {
          el.style.setProperty('--ac-depth-x', `${(x * 4).toFixed(2)}px`);
          el.style.setProperty('--ac-depth-y', `${(y * 3).toFixed(2)}px`);
          el.style.setProperty('--ac-depth-r', `${(x * 2).toFixed(2)}deg`);
        } else {
          el.style.setProperty('--ac-card-x', `${(-y * 2.2).toFixed(2)}deg`);
          el.style.setProperty('--ac-card-y', `${(x * 2.2).toFixed(2)}deg`);
        }
      }));
    }, { passive: true });
    el.addEventListener('pointerleave', () => resetDepth(el));
    el.addEventListener('pointercancel', () => resetDepth(el));
  };
  const bindLogoFallback = (img) => {
    if (initialized.has(img)) return;
    initialized.add(img);
    const failed = () => {
      // The visible brand caption remains a working, descriptive link.
      const art = img.closest('.ac-brand-logo-art');
      if (!art || art.querySelector('.ac-brand-type')) return;
      img.hidden = true;
      const crop = img.closest('.ac-robots-logo-crop'); if (crop) crop.hidden = true;
      const label = document.createElement('span');
      label.className = 'ac-brand-type'; label.textContent = img.alt || 'Explore the brand';
      art.appendChild(label);
    };
    img.addEventListener('error', failed, { once: true });
    if (img.complete && img.naturalWidth === 0) failed();
  };
  const setupReveals = (scope) => {
    if (mode() !== 'enhanced' || reduce.matches || !('IntersectionObserver' in window)) return;
    if (!observer) observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        if (mode() === 'enhanced' && !reduce.matches) entry.target.classList.add('ac-reveal-once');
        observer.unobserve(entry.target);
      });
    }, { threshold: .08 });
    scope.querySelectorAll('.ac-fandom-grid, .ac-brand-features--expanded, .ac-confidence-section').forEach(el => {
      if (revealed.has(el)) return;
      revealed.add(el); observer.observe(el);
    });
  };
  const init = (scope = document) => {
    scope.querySelectorAll('.ac-mega').forEach(bindMenu);
    scope.querySelectorAll('[data-depth-stage], [data-depth-card]').forEach(bindDepth);
    scope.querySelectorAll('img[data-logo-fallback]').forEach(bindLogoFallback);
    setupReveals(scope); measureHeader();
  };
  let resizeFrame;
  window.addEventListener('resize', () => { cancelAnimationFrame(resizeFrame); resizeFrame = requestAnimationFrame(measureHeader); }, { passive: true });
  desktop.addEventListener('change', () => {
    closeDesktopMenus(); measureHeader();
    const mobile = document.getElementById('ACMobileMenu');
    if (desktop.matches && mobile?.open) mobile.close();
  });
  const motionChanged = () => {
    if (reduce.matches) observer?.disconnect();
    document.querySelectorAll('[data-depth-stage], [data-depth-card]').forEach(resetDepth);
  };
  reduce.addEventListener('change', motionChanged); pointer.addEventListener('change', motionChanged);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', () => { closeDesktopMenus(); measureHeader(); });
  document.addEventListener('visibilitychange', () => { if (document.hidden) motionChanged(); });
  init();
})();
